import type { VaultItem } from '@heybesafe/shared';

export interface ResolvedLinks {
  contacts: VaultItem[];
  documents: VaultItem[];
  schedules: VaultItem[];
  /** Family-shared Safe items (login / note / card / identity). Passkeys are
   *  deliberately absent: a passkey belongs to a login rather than standing on
   *  its own, so it is not offered by the picker or resolved here. */
  safeItems: VaultItem[];
}

/** Safe item types a document may link. Kept beside the resolver so the picker
 *  and the resolver cannot drift into offering something that never renders. */
const LINKABLE_SAFE_TYPES = ['login', 'note', 'card', 'identity'] as const;

/**
 * Resolve a document's `relatedItemIds` to the linked items the current viewer
 * can actually see: org-scoped items in the SAME family that are present in the
 * decrypted `items` set — Binder items (contacts / documents / schedules) and
 * family-shared Safe items (login / note / card / identity). Ids that are missing, personal, or in another org are
 * silently skipped so a member never sees a broken link. Order within each type
 * bucket follows `relatedItemIds`.
 *
 * `reverseScheduleForId` additionally pulls in **reverse** links: schedules
 * whose singular `relatedItemId` points AT the item being viewed. A schedule is
 * associated with a document from the schedule's own editor (`relatedItemId`),
 * so without this the document would never show the schedule that references it.
 * These are appended after any forward-linked schedules, de-duplicated by id.
 */
export function resolveLinkedItems(
  relatedItemIds: string[] | undefined,
  items: VaultItem[],
  orgId: string,
  reverseScheduleForId?: string,
): ResolvedLinks {
  const out: ResolvedLinks = { contacts: [], documents: [], schedules: [], safeItems: [] };
  const byId = new Map(items.filter((i) => i.orgId === orgId).map((i) => [i.id, i]));
  const seen = new Set<string>();
  for (const id of relatedItemIds ?? []) {
    const item = byId.get(id);
    if (!item || seen.has(id)) continue;
    seen.add(id);
    if (item.type === 'contact') out.contacts.push(item);
    else if (item.type === 'document') out.documents.push(item);
    else if (item.type === 'schedule') out.schedules.push(item);
    else if ((LINKABLE_SAFE_TYPES as readonly string[]).includes(item.type)) out.safeItems.push(item);
  }
  if (reverseScheduleForId) {
    for (const item of items) {
      if (item.orgId === orgId && item.type === 'schedule'
          && item.relatedItemId === reverseScheduleForId && !seen.has(item.id)) {
        seen.add(item.id);
        out.schedules.push(item);
      }
    }
  }
  return out;
}
